/**
 * FAQ.tsx — Public FAQ page.
 * Rebuilt in Pass 7.
 *
 * Preserves all 13 sections and ~80 Q&A items verbatim from the original.
 * New structure:
 *   - Sticky sidebar TOC (auto-highlights as user scrolls)
 *   - Accordion items (click to expand/collapse)
 *   - Search bar at the top to filter across all sections
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const C = {
  bgBase: '#080C18',
  bgPanel: '#0B0F1F',
  bgCard: '#0F1628',
  bgElevated: '#141C33',
  borderSubtle: 'rgba(148, 163, 184, 0.08)',
  borderDefault: 'rgba(148, 163, 184, 0.12)',
  borderStrong: 'rgba(148, 163, 184, 0.20)',
  textPrimary: '#E8EFF8',
  textSecondary: '#94A3B8',
  textTertiary: '#64748B',
  textQuaternary: '#475569',
  cyan: '#00D4FF',
  cyanLight: '#7DE5F5',
  cyanSoft: 'rgba(0, 212, 255, 0.10)',
  cyanGlow: 'rgba(0, 212, 255, 0.35)',
};

const F = { ui: "'DM Sans', -apple-system, system-ui, sans-serif" };

interface FAQItem { q: string; a: string | React.ReactNode }
interface FAQSection { id: string; title: string; items: FAQItem[] }

/* ═══════════════ CONTENT ═══════════════
 * Preserved verbatim from the original FAQ.tsx.
 */
const sections: FAQSection[] = [
  {
    id: 'getting-started',
    title: '1. Getting started',
    items: [
      { q: 'What is Meridian Registry?', a: 'Meridian Registry is a master data platform that identifies, resolves, and permanently tracks every physical retail outlet across your market. It turns fragmented outlet data from multiple sources into a single source of truth — enabling accurate distribution measurement and gap analysis that no single company can achieve alone.' },
      { q: 'How does Meridian Registry work?', a: 'You submit your outlet database. The platform automatically matches your records against the full verified registry using business identifiers, name and address comparison, and GPS proximity. Within minutes, you see which of your outlets are already known, which are new to the market, and where your coverage gaps are.' },
      { q: 'Who is Meridian Registry for?', a: 'Meridian is built for FMCG manufacturers, distributors, and market research companies that maintain outlet databases and need to understand numeric distribution, identify coverage gaps, or reconcile data across sources.' },
      { q: 'What should I do after my first login?', a: 'Start on My Dashboard for an overview of your matched outlets, match rate, and pending reviews. Then visit Distribution Gaps to generate your first gap analysis and see your Distribution Coverage score.' },
    ],
  },
  {
    id: 'your-data',
    title: '2. Your data',
    items: [
      { q: 'How do I submit my outlet data?', a: 'Upload your data through the Upload page or send it to your account manager. We accept standard spreadsheet formats including Excel and CSV.' },
      { q: 'What happens after I upload my data?', a: 'Your data is automatically processed through the matching pipeline. You will receive an email notification when processing is complete, and results will appear on My Dashboard immediately.' },
      { q: 'What data formats are supported?', a: 'Meridian accepts Excel (.xlsx) and CSV files. The upload process includes a column mapping step so your file does not need to follow a fixed template.' },
      { q: 'Is my data secure?', a: 'Your data is encrypted in transit and at rest. Your raw submissions are never shared with or visible to any other client. Only aggregate corroboration counts appear in gap analysis — never the identity of contributing sources.' },
      { q: 'What happens to my submitted data?', a: 'Your original records are preserved exactly as submitted. Matched records are linked to permanent registry identifiers. Where your data fills gaps in the canonical record — such as a GPS coordinate or a more complete address — it enriches the registry for all participants.' },
      { q: 'Can I submit data for multiple time periods?', a: 'Yes. Submitting updated data over time enables Market Intelligence features such as new outlet detection, closure tracking, and coverage trend analysis. We recommend a monthly cadence.' },
    ],
  },
  {
    id: 'matching-registry',
    title: '3. Matching & registry',
    items: [
      { q: 'How does matching work?', a: 'The platform runs three matching passes in sequence. Exact Match verifies business registration identifiers with certainty. Smart Match uses name similarity, address comparison, and GPS proximity scoring. GPS Match resolves outlets that are physically close but described differently across databases. Genuinely new outlets receive a permanent identifier and enter the registry.' },
      { q: 'What is a Census ID?', a: 'A Census ID is a permanent, unique identifier assigned to every physical outlet in the registry. Once assigned, it never changes — regardless of name changes, address updates, or ownership transfers. It is the stable key that links records across all sources.' },
      { q: 'What match rates should I expect?', a: 'Match rates above 95% indicate excellent data quality and strong overlap with the existing registry. Rates below 80% typically indicate formatting issues, missing fields, or a large number of outlets genuinely new to the registry.' },
      { q: 'What is the difference between Exact Match, Smart Match, and GPS Match?', a: 'Exact Match confirms identity through verified business registration numbers. Smart Match uses name similarity, address comparison, and location data to find likely matches. GPS Match resolves outlets within close physical proximity that have different names or addresses across databases.' },
      { q: 'How does the engine handle different languages and scripts?', a: 'The matching engine supports multiple scripts and character sets. It automatically transliterates between writing systems, removes legal suffixes, expands abbreviations, and standardises formatting differences so records from different sources match correctly.' },
      { q: 'What is a confidence score?', a: 'Each match receives a confidence score reflecting how certain the platform is that two records refer to the same physical outlet. High-confidence matches are confirmed automatically. Lower-confidence matches are sent to the Review Queue for human verification.' },
    ],
  },
  {
    id: 'understanding-dashboard',
    title: '4. Understanding your dashboard',
    items: [
      { q: 'What does My Dashboard show?', a: 'My Dashboard is your overview of how your submitted data maps to the registry. It displays summary cards for your key metrics and a searchable table of your matched outlets below.' },
      { q: 'What does "Your Outlets" mean?', a: 'Your Outlets is the number of distinct physical locations in the registry that have been successfully matched to records you submitted. Each outlet is counted once regardless of how many of your records resolved to it.' },
      { q: 'What is Match Rate?', a: 'Match Rate is the percentage of your submitted records that resolved to a registry outlet. It is the primary indicator of your data quality and overlap with the existing registry.' },
      { q: 'What does Pending Review mean?', a: 'Pending Review counts match candidates where the platform found a plausible connection but needs human confirmation. Visit the Review Queue to approve or reject these items.' },
      { q: 'What is Registry Outlets?', a: 'Registry Outlets is the total number of unique physical retail locations in the Census Database. This number grows continuously as new outlets are discovered through submissions.' },
      { q: 'What does Matched Outlets show?', a: 'Matched Outlets counts the distinct registry outlets that have been successfully identified across all submissions. A single outlet matched by three different sources counts once.' },
      { q: 'What is Client Records?', a: 'Client Records is the total number of raw outlet records submitted across all sources. This differs from Matched Outlets because multiple sources can submit records for the same physical outlet.' },
    ],
  },
  {
    id: 'distribution-gaps',
    title: '5. Distribution gaps',
    items: [
      { q: 'What is a distribution gap?', a: 'A distribution gap is a retail outlet that exists in the registry and is covered by at least one other source — but is absent from your database. Each gap represents a real physical store where competitors are present and you are not.' },
      { q: 'How does Meridian identify my gaps?', a: 'The platform compares your matched outlets against the full registry. Any outlet that at least one other source covers but you do not is flagged as a gap.' },
      { q: 'How do I generate my gap analysis?', a: 'On the Distribution Gaps page, select your filters (country, outlet type, and optionally city), then click Generate. Your gap list and Distribution Coverage score appear based on your selected scope.' },
      { q: 'How do I export my gaps?', a: 'Click "Export Route List" on the Distribution Gaps page. This downloads a spreadsheet with outlet name, address, city, and location details — ready for your field team to act on.' },
      { q: 'What is Distribution Coverage?', a: 'Distribution Coverage is the percentage of relevant registry outlets you are actively covering within your selected scope. A score of 34% in supermarkets means you are present in roughly one in three relevant outlets.' },
      { q: 'What is a good Distribution Coverage score?', a: 'That depends on your category, market maturity, and commercial goals. The platform tracks your score over time. The gap table shows exactly which outlets to target to improve it.' },
      { q: 'Can other clients see which outlets I cover?', a: 'No. Gap detection shows aggregate corroboration counts only — never which specific sources reported an outlet. No other participant can see your outlet list.' },
    ],
  },
  {
    id: 'review-queue',
    title: '6. Review queue',
    items: [
      { q: 'What is the Review Queue?', a: 'The Review Queue presents match candidates that need human confirmation. Each item shows your submitted record compared side-by-side against the Meridian Registry record so you can determine whether they refer to the same physical outlet.' },
      { q: 'Why do some records need manual review?', a: 'When the platform finds plausible but uncertain matches — partial name overlaps, abbreviated addresses, or missing location data — it flags them for human review rather than risking an incorrect automatic decision.' },
      { q: 'How do I approve or reject a match?', a: 'Each review item shows two records compared side-by-side. Click "Approve" if they are the same physical outlet, or "Reject" if they are different locations.' },
      { q: 'What is Batch Review?', a: 'Batch Review lets you group similar review items and approve or reject them together. This is significantly faster when reviewing chain outlets or records that follow a clear pattern.' },
      { q: 'What happens after I make a review decision?', a: 'Approved matches are permanently linked under a single Census ID. Rejected records receive separate identifiers. Every decision trains the platform, adjusting confidence thresholds and reducing the volume of future reviews.' },
      { q: 'Will the same match appear in my queue again?', a: 'No. Once approved or rejected, the decision is permanently recorded. That pair is skipped in all future processing cycles.' },
    ],
  },
  {
    id: 'market-intelligence',
    title: '7. Market intelligence',
    items: [
      { q: 'What does Market Intelligence show?', a: 'Market Intelligence tracks how the retail landscape evolves over time — new outlets entering the market, probable closures, and shifts in coverage. It is your early-warning system for market changes.' },
      { q: 'Why does Market Intelligence need multiple data cycles?', a: 'The platform detects changes by comparing your current submission against previous ones. At least two data submission cycles are required before new outlet detection, closure tracking, and coverage trends become available.' },
      { q: 'How does closure detection work?', a: 'When an outlet that was previously reported by multiple sources stops appearing in new submissions, it is flagged as a probable closure. The platform uses a conservative threshold to avoid false positives from temporary data gaps.' },
      { q: 'How often should I submit updated data?', a: 'We recommend a monthly cadence to keep your gap analysis current, your coverage score accurate, and Market Intelligence trends meaningful.' },
    ],
  },
  {
    id: 'registry-map',
    title: '8. Registry map',
    items: [
      { q: 'What does the Registry Map show?', a: 'The Registry Map displays outlets with GPS coordinates on an interactive map. You can view your matched outlets, identify coverage clusters, and spot geographic gaps in your distribution.' },
      { q: `Why don't all outlets appear on the map?`, a: 'Only outlets with GPS coordinates are displayed. Not all outlets in the registry have location data — GPS coverage improves over time as more sources contribute coordinate information.' },
      { q: 'What is the gap overlay?', a: 'The gap overlay highlights outlets that are in the registry but absent from your database. It turns your distribution gaps into a visual geographic view, making it easy to identify underserved areas.' },
      { q: 'What is the anomaly layer?', a: 'The anomaly layer flags outlets with data inconsistencies — such as coordinates that do not match the stated city, or duplicate entries at the same location. It helps maintain registry data quality.' },
    ],
  },
  {
    id: 'data-quality',
    title: '9. Data quality & enrichment',
    items: [
      { q: 'How does outlet data improve over time?', a: `Each client submission enriches the registry. When your data fills a missing field — a GPS coordinate, a more complete address — the canonical record improves for all participants. No client's existing data is overwritten.` },
      { q: 'What is the Data Sources section in outlet detail?', a: 'The Data Sources section shows which independent sources contributed data to each field of an outlet record. Administrators see full attribution including source names. Client users see anonymized confirmation counts only — no other client names are revealed.' },
      { q: 'What does "Confirmed by N sources" mean?', a: 'It means that N independent data sources have contributed information for that field. Higher source counts indicate greater data reliability. This is visible on any outlet detail page.' },
    ],
  },
  {
    id: 'exports-api',
    title: '10. Exports & API',
    items: [
      { q: 'What does my export file contain?', a: 'Your export is a spreadsheet containing your original data enriched with registry fields — Census ID, standardised name, address, match type, and confidence score for every record.' },
      { q: 'Is there an API?', a: 'Yes. Meridian provides an authenticated REST API for programmatic access to registry data. The API supports outlet lookups, search, and integration with your existing ERP or reporting systems.' },
      { q: 'What can I do with the API?', a: 'You can look up any outlet by Census ID, search outlets by name or city, and retrieve coverage information. The API is designed for direct integration into your business systems.' },
    ],
  },
  {
    id: 'account-access',
    title: '11. Account & access',
    items: [
      { q: 'What are the user roles?', a: 'There are four roles: Owner/Admin with full platform access and user management, Client Admin with full data access for their organisation, Analyst with read access to dashboards and exports, and Reviewer with access limited to the Review Queue.' },
      { q: 'What can each role do?', a: `Owners and Admins manage users, run pipelines, and access all data. Client Admins see their own organisation's full data. Analysts can view dashboards, gaps, and exports but cannot modify data. Reviewers can only approve or reject matches in the Review Queue.` },
      { q: 'How are user roles and permissions managed?', a: 'Administrators assign roles through the User Management page. Each user is associated with a specific client organisation and receives access scoped to their role. Permissions are enforced server-side — they cannot be bypassed by the client application.' },
    ],
  },
  {
    id: 'atlas',
    title: '12. Atlas',
    items: [
      { q: 'What is Atlas?', a: `Atlas is Meridian Registry's AI-powered data analyst, available on every page of the platform. Ask questions about your outlet data in plain language and receive answers backed by live registry data — no dashboards to navigate, no filters to configure.` },
      { q: 'What can I ask Atlas?', a: 'Atlas can answer questions about your distribution coverage, surface your highest-priority gaps, summarise market changes since your last data cycle, look up specific outlets by name or location, and help you build route lists for your field team. If the data is in the registry, Atlas can find it.' },
      { q: 'How do I access Atlas?', a: 'Look for the Atlas button on the right side of any dashboard page. Your conversation history is maintained throughout your session.' },
      { q: `Can Atlas see other organisations' data?`, a: 'No. Atlas operates within the same data boundaries as the rest of the platform. It only surfaces data you are authorised to see and never reveals information about other participants.' },
      { q: `How accurate are Atlas's answers?`, a: 'Atlas pulls directly from live registry data — the same data powering your dashboards. Its answers are as current as your most recent data submission. Atlas will tell you when it cannot find data rather than guessing.' },
      { q: 'Can I ask Atlas to export data?', a: 'Atlas can help you identify what to export and where to find it, but downloads are handled through the Export page. This ensures all exports are properly logged and controlled.' },
    ],
  },
  {
    id: 'platform-security',
    title: '13. Platform & security',
    items: [
      { q: 'How is client data isolated?', a: `Every client's data is strictly separated at the database level. No client can see another client's outlet list, raw submissions, or company name. Gap analysis uses only aggregate counts.` },
      { q: 'What is the Audit Log?', a: 'The Audit Log records every significant action on the platform — data uploads, match decisions, user changes, and pipeline runs. It provides a complete, timestamped trail of who did what and when.' },
      { q: 'Can I see who made changes to my data?', a: 'Yes. The Audit Log tracks all review decisions, data modifications, and administrative actions with the user, timestamp, and details of each change.' },
      { q: 'What compliance standards does Meridian meet?', a: 'Meridian implements encrypted storage, authenticated API access with rate limiting, role-based access controls, and cryptographic hashing of all API keys. Contact your account manager for detailed compliance documentation.' },
      { q: 'How is my data retained?', a: 'Data is retained to maintain the integrity and continuity of the registry. Historical records enable Market Intelligence features including coverage trend analysis and closure detection. Contact your account manager to discuss specific retention requirements.' },
    ],
  },
];

/* ═══════════════ PAGE ═══════════════ */

export default function FAQ() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [activeSection, setActiveSection] = useState(sections[0].id);
  const [search, setSearch] = useState('');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    document.title = 'FAQ | Meridian Registry';
    if (!document.querySelector('link[href*="DM+Sans"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  // Highlight active section as user scrolls
  const handleScroll = useCallback(() => {
    const offset = 140;
    for (let i = sections.length - 1; i >= 0; i--) {
      const el = sectionRefs.current[sections[i].id];
      if (el && el.getBoundingClientRect().top <= offset) {
        setActiveSection(sections[i].id);
        return;
      }
    }
    setActiveSection(sections[0].id);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const toggle = (key: string) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const scrollTo = (id: string) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Filter sections + items by search query. When searching, auto-expand matches.
  const filtered = useMemo(() => {
    if (!search.trim()) return sections;
    const q = search.toLowerCase();
    return sections
      .map(section => ({
        ...section,
        items: section.items.filter(item =>
          item.q.toLowerCase().includes(q)
          || (typeof item.a === 'string' && item.a.toLowerCase().includes(q))
        ),
      }))
      .filter(section => section.items.length > 0);
  }, [search]);

  // Auto-expand items when search is active
  useEffect(() => {
    if (!search.trim()) return;
    const newExpanded: Record<string, boolean> = {};
    filtered.forEach(section => {
      section.items.forEach((_, itemIdx) => {
        newExpanded[`${section.id}-${itemIdx}`] = true;
      });
    });
    setExpanded(newExpanded);
  }, [search, filtered]);

  return (
    <div style={{
      background: C.bgBase,
      color: C.textPrimary,
      fontFamily: F.ui,
      minHeight: '100vh',
      position: 'relative',
      WebkitFontSmoothing: 'antialiased',
    }}>
      {/* Ambient glow */}
      <div aria-hidden style={{
        position: 'fixed',
        top: -300, right: -400,
        width: 900, height: 900,
        background: 'radial-gradient(circle, rgba(0,212,255,0.06) 0%, transparent 65%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* Nav */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(8, 12, 24, 0.72)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${C.borderSubtle}`,
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          padding: '14px 32px',
          display: 'flex', alignItems: 'center',
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10, padding: 0,
            }}>
            <img alt="Meridian" src="/Meridian_M_logo-transparent.png" style={{ height: 28 }} />
            <span style={{
              fontSize: 14, fontWeight: 700,
              letterSpacing: '0.22em',
              color: C.textPrimary, textTransform: 'uppercase',
            }}>Meridian</span>
          </button>
          <span style={{
            marginLeft: 16, paddingLeft: 16,
            borderLeft: `1px solid ${C.borderDefault}`,
            fontSize: 13, color: C.textSecondary,
          }}>FAQ</span>
        </div>
      </nav>

      {/* Main grid: sidebar + content */}
      <div style={{
        maxWidth: 1280, margin: '0 auto',
        padding: '60px 32px 80px',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 40,
        position: 'relative', zIndex: 1,
      }}
      className="meridian-faq-grid">
        <style>{`
          @media (min-width: 900px) {
            .meridian-faq-grid {
              grid-template-columns: 240px 1fr !important;
              gap: 64px !important;
            }
          }
        `}</style>

        {/* Sticky TOC sidebar */}
        <aside style={{ position: 'relative' }}>
          <div style={{
            position: 'sticky', top: 100,
            display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 600,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: C.textTertiary, marginBottom: 12,
            }}>On this page</div>
            {sections.map(s => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                style={{
                  textAlign: 'left',
                  padding: '6px 12px',
                  fontSize: 13, fontWeight: 500,
                  color: activeSection === s.id ? C.cyan : C.textSecondary,
                  background: activeSection === s.id ? C.cyanSoft : 'transparent',
                  border: 'none',
                  borderLeft: `2px solid ${activeSection === s.id ? C.cyan : 'transparent'}`,
                  borderRadius: '0 6px 6px 0',
                  cursor: 'pointer',
                  fontFamily: F.ui,
                  transition: 'all 120ms ease',
                }}
                onMouseEnter={e => { if (activeSection !== s.id) e.currentTarget.style.color = C.textPrimary; }}
                onMouseLeave={e => { if (activeSection !== s.id) e.currentTarget.style.color = C.textSecondary; }}
              >{s.title}</button>
            ))}
          </div>
        </aside>

        {/* Content */}
        <div style={{ minWidth: 0 }}>
          <h1 style={{
            fontSize: 40, fontWeight: 600,
            letterSpacing: '-0.03em',
            marginBottom: 12,
            margin: '0 0 12px 0',
          }}>Frequently asked questions</h1>
          <div aria-hidden style={{
            height: 3, width: 56,
            background: C.cyan,
            borderRadius: 2,
            boxShadow: `0 0 12px ${C.cyanGlow}`,
            marginBottom: 28,
          }} />

          {/* Search */}
          <div style={{ position: 'relative', marginBottom: 32 }}>
            <svg
              width={16} height={16}
              viewBox="0 0 24 24"
              fill="none" stroke={C.textTertiary} strokeWidth={2}
              strokeLinecap="round" strokeLinejoin="round"
              style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }}
            >
              <circle cx={11} cy={11} r={8} />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search frequently asked questions…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={e => { e.currentTarget.style.borderColor = C.cyan; e.currentTarget.style.boxShadow = `0 0 0 3px ${C.cyanSoft}`; }}
              onBlur={e => { e.currentTarget.style.borderColor = C.borderDefault; e.currentTarget.style.boxShadow = 'none'; }}
              style={{
                width: '100%',
                background: C.bgCard,
                border: `1px solid ${C.borderDefault}`,
                borderRadius: 10,
                padding: '12px 14px 12px 42px',
                color: C.textPrimary,
                fontFamily: F.ui, fontSize: 14,
                outline: 'none',
                transition: 'all 150ms ease',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {filtered.length === 0 ? (
            <div style={{
              background: C.bgPanel,
              border: `1px solid ${C.borderDefault}`,
              borderRadius: 12,
              padding: 48,
              textAlign: 'center',
              color: C.textSecondary,
              fontSize: 14,
            }}>
              No questions match your search. Try{' '}
              <button
                onClick={() => setSearch('')}
                style={{
                  background: 'none', border: 'none',
                  color: C.cyanLight, cursor: 'pointer',
                  textDecoration: 'underline', fontSize: 14, fontFamily: F.ui,
                }}>clearing the search</button>
              {' '}or{' '}
              <a href="mailto:admin@meridianregistry.com" style={{ color: C.cyanLight, textDecoration: 'none' }}>
                email us
              </a>.
            </div>
          ) : (
            filtered.map(section => (
              <section
                key={section.id}
                ref={el => { sectionRefs.current[section.id] = el; }}
                style={{ marginBottom: 40, scrollMarginTop: 100 }}
              >
                <h2 style={{
                  fontSize: 22, fontWeight: 600,
                  color: C.textPrimary,
                  marginBottom: 12,
                  letterSpacing: '-0.015em',
                  margin: '0 0 12px 0',
                }}>{section.title}</h2>

                <div style={{
                  background: C.bgPanel,
                  border: `1px solid ${C.borderDefault}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                }}>
                  {section.items.map((item, i) => {
                    const key = `${section.id}-${i}`;
                    const isOpen = expanded[key];
                    return (
                      <div key={key} style={{
                        borderTop: i === 0 ? 'none' : `1px solid ${C.borderSubtle}`,
                      }}>
                        <button
                          onClick={() => toggle(key)}
                          style={{
                            width: '100%',
                            background: 'transparent', border: 'none',
                            padding: '16px 20px',
                            cursor: 'pointer',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            gap: 16, textAlign: 'left',
                            fontFamily: F.ui,
                          }}>
                          <span style={{
                            fontSize: 15, fontWeight: 500,
                            color: C.textPrimary,
                            lineHeight: 1.5,
                          }}>{item.q}</span>
                          <svg
                            width={16} height={16} viewBox="0 0 24 24"
                            fill="none" stroke={isOpen ? C.cyan : C.textTertiary}
                            strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"
                            style={{
                              flexShrink: 0,
                              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 150ms ease',
                            }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                        {isOpen && (
                          <div style={{
                            padding: '0 20px 18px',
                            fontSize: 14,
                            color: C.textSecondary,
                            lineHeight: 1.7,
                          }}>
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))
          )}

          {/* Bottom CTA */}
          <div style={{
            marginTop: 60,
            padding: 32,
            background: C.bgPanel,
            border: `1px solid ${C.borderDefault}`,
            borderRadius: 14,
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: 11, fontWeight: 600,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: C.cyan, marginBottom: 8,
            }}>Still have questions?</div>
            <h3 style={{
              fontSize: 20, fontWeight: 600,
              color: C.textPrimary,
              marginBottom: 14,
              margin: '0 0 14px 0',
            }}>We're happy to help.</h3>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/request-demo" style={{
                fontFamily: F.ui, fontWeight: 600, fontSize: 14,
                padding: '11px 22px', borderRadius: 10,
                background: C.cyan, color: '#000',
                border: `1px solid ${C.cyan}`,
                textDecoration: 'none',
                boxShadow: `0 0 0 1px ${C.cyanGlow}, 0 8px 24px -8px ${C.cyanGlow}`,
              }}>Request a demo</Link>
              <a href="mailto:admin@meridianregistry.com" style={{
                fontFamily: F.ui, fontWeight: 500, fontSize: 14,
                padding: '11px 22px', borderRadius: 10,
                background: 'transparent', color: C.textSecondary,
                border: `1px solid ${C.borderDefault}`,
                textDecoration: 'none',
              }}>Email support</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
